import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {FaChevronLeft,FaChevronRight,FaFileExcel,FaSearch,} from "react-icons/fa";

import { REACT_APP_API_URL } from "../../../../../env";

const DEFAULT_COUNT = 100;

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCurrentMonthStart = () => {
  const today = new Date();
  return formatDateInput(new Date(today.getFullYear(), today.getMonth(), 1));
};

const getInitialFilters = () => ({
  fromDate: getCurrentMonthStart(),
  toDate: formatDateInput(new Date()),
  count: DEFAULT_COUNT,
  hotelId: "",
  hotelName: "",
});

const toUnixSeconds = (dateValue, endOfDay = false) => {
  if (!dateValue) return "";

  const [year, month, day] = dateValue.split("-").map(Number);
  const utcMillis = endOfDay
    ? Date.UTC(year, month - 1, day, 23, 59, 59)
    : Date.UTC(year, month - 1, day, 0, 0, 0);

  return Math.floor(utcMillis / 1000);
};

const formatAmount = (value) => {
  if (!Number.isFinite(Number(value))) return "-";

  return `INR ${Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const toExportNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : "";
};

const getHotelId = (hotel) => hotel?._id || hotel?.id || hotel?.hotelId || "";

const getHotelName = (hotel) => hotel?.name || hotel?.hotelName || hotel?.title || "";

const getHotelValue = (hotel) => getHotelId(hotel) || getHotelName(hotel);

const getPaymentHotelId = (item) =>
  getHotelId(item?.hotel) || item?.hotelId || item?.hotel_id || "";

const getPaymentHotelName = (item) =>
  getHotelName(item?.hotel) || item?.hotelName || item?.hotel_name || "";

const matchesHotelFilter = (item, reportFilters) => {
  if (!reportFilters.hotelId && !reportFilters.hotelName) return true;

  const paymentHotelId = getPaymentHotelId(item);
  const paymentHotelName = getPaymentHotelName(item);

  return Boolean(
    (reportFilters.hotelId && paymentHotelId === reportFilters.hotelId) ||
      (reportFilters.hotelName &&
        paymentHotelName.toLowerCase() === reportFilters.hotelName.toLowerCase())
  );
};

const getArrayFromResponse = (data) => {
  if (Array.isArray(data)) return data;

  return [];
};

const mergeHotels = (currentHotels, nextHotels) => {
  const hotelMap = new Map();

  [...currentHotels, ...nextHotels].forEach((hotel) => {
    const hotelId = getHotelId(hotel);
    const hotelName = getHotelName(hotel);
    const key = hotelId || hotelName;

    if (key && !hotelMap.has(key)) {
      hotelMap.set(key, hotel);
    }
  });

  return Array.from(hotelMap.values()).sort((firstHotel, secondHotel) =>
    getHotelName(firstHotel).localeCompare(getHotelName(secondHotel))
  );
};

const formatDate = (value, withTime = false) => {
  if (value === null || value === undefined || value === "") return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return withTime
    ? date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const getStatusBadgeClass = (status) => {
  if (status === "captured") return "bg-success";
  if (status === "failed") return "bg-danger";
  if (status === "refunded") return "bg-warning text-dark";

  return "bg-secondary";
};

const PaymentReportTable = () => {
  const [filters, setFilters] = useState(getInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);
  const [hotels, setHotels] = useState([]);
  const [skip, setSkip] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [razorpayCount, setRazorpayCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const hasLoadedInitialReport = useRef(false);

  const fetchPayments = useCallback(
    async (nextSkip = 0, reportFilters = appliedFilters) => {
      if (!reportFilters.fromDate || !reportFilters.toDate) {
        setError("Please select From Date and To Date.");
        return;
      }

      if (new Date(reportFilters.fromDate) > new Date(reportFilters.toDate)) {
        setError("From Date cannot be after To Date.");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await axios.get(`${REACT_APP_API_URL}api/payment/all`, {
          params: {
            count: Number(reportFilters.count) || DEFAULT_COUNT,
            skip: nextSkip,
            from: toUnixSeconds(reportFilters.fromDate),
            to: toUnixSeconds(reportFilters.toDate, true),
            ...(reportFilters.hotelId ? { hotelId: reportFilters.hotelId } : {}),
            ...(!reportFilters.hotelId && reportFilters.hotelName
              ? { hotelName: reportFilters.hotelName }
              : {}),
          },
        });

        const responseRows = Array.isArray(response.data?.items)
          ? response.data.items
          : [];
        const filteredRows = responseRows.filter((item) =>
          matchesHotelFilter(item, reportFilters)
        );

        setRows(filteredRows);
        setHotels((currentHotels) =>
          mergeHotels(
            currentHotels,
            responseRows.map((item) => item.hotel).filter(Boolean)
          )
        );
        setTotalCount(
          reportFilters.hotelId || reportFilters.hotelName
            ? filteredRows.length
            : Number(response.data?.count) || responseRows.length
        );
        setRazorpayCount(Number(response.data?.meta?.razorpayCount) || 0);
        setSkip(nextSkip);
      } catch (err) {
        console.error("Error fetching payment report:", err);
        setRows([]);
        setTotalCount(0);
        setRazorpayCount(0);
        setError("Failed to load payment report.");
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters]
  );

  useEffect(() => {
    if (hasLoadedInitialReport.current) return;

    hasLoadedInitialReport.current = true;
    const initialFilters = getInitialFilters();
    setAppliedFilters(initialFilters);
    fetchPayments(0, initialFilters);
  }, [fetchPayments]);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await axios.get(`${REACT_APP_API_URL}api/hotel/all`);
        setHotels((currentHotels) =>
          mergeHotels(currentHotels, getArrayFromResponse(response.data))
        );
      } catch (err) {
        console.error("Error fetching hotels:", err);
        setHotels([]);
      }
    };

    fetchHotels();
  }, []);

  const pageNumber =
    Math.floor(skip / (Number(appliedFilters.count) || DEFAULT_COUNT)) + 1;
  const canGoPrevious = skip > 0 && !loading;
  const canGoNext =
    !loading &&
    rows.length > 0 &&
    (totalCount > skip + rows.length ||
      rows.length === Number(appliedFilters.count));

  const handleFilterChange = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleHotelChange = (value) => {
    const selectedHotel = hotels.find((hotel) => getHotelValue(hotel) === value);

    setFilters((current) => ({
      ...current,
      hotelId: getHotelId(selectedHotel),
      hotelName: getHotelName(selectedHotel),
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    fetchPayments(0, filters);
  };

  const handlePrevious = () => {
    const count = Number(appliedFilters.count) || DEFAULT_COUNT;
    fetchPayments(Math.max(0, skip - count), appliedFilters);
  };

  const handleNext = () => {
    const count = Number(appliedFilters.count) || DEFAULT_COUNT;
    fetchPayments(skip + count, appliedFilters);
  };

  const handleExportExcel = () => {
    const exportRows = rows.map((item, index) => ({
      "#": skip + index + 1,
      "Payment ID": item.paymentId || "",
      "Booking Ref": item.bookingRef || "",
      Status: item.status || "",
      Captured: item.captured ? "Yes" : "No",
      Amount: toExportNumber(item.amount),
      Refunded: toExportNumber(item.amountRefunded),
      "Refund Status": item.refundStatus || "",
      Customer: item.customer?.name || "",
      Email: item.customer?.email || "",
      Mobile: item.customer?.mobile || "",
      Hotel: item.hotel?.name || "",
      "Check In": formatDate(item.stay?.checkIn),
      "Check Out": formatDate(item.stay?.checkOut),
      "Created At": formatDate(item.createdAt, true),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 26 },
      { wch: 18 },
      { wch: 12 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 24 },
      { wch: 28 },
      { wch: 16 },
      { wch: 28 },
      { wch: 14 },
      { wch: 14 },
      { wch: 22 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payment Report");
    XLSX.writeFile(
      workbook,
      `payment-report-${appliedFilters.fromDate}-to-${appliedFilters.toDate}.xlsx`
    );
  };

  return (
    <div>
      <div className="row g-3 mb-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.fromDate}
            max={filters.toDate || undefined}
            onChange={(event) => handleFilterChange("fromDate", event.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.toDate}
            min={filters.fromDate || undefined}
            onChange={(event) => handleFilterChange("toDate", event.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">Hotel</label>
          <select
            className="form-select"
            value={filters.hotelId || filters.hotelName}
            onChange={(event) => handleHotelChange(event.target.value)}
          >
            <option value="">All Hotels</option>
            {hotels.map((hotel) => {
              const hotelValue = getHotelValue(hotel);
              const hotelName = getHotelName(hotel);

              return (
                <option key={hotelValue} value={hotelValue}>
                  {hotelName || hotelValue}
                </option>
              );
            })}
          </select>
        </div>
        <div className="col-md-1">
          <label className="form-label">Count</label>
          <input
            type="number"
            min="1"
            max="500"
            className="form-control"
            value={filters.count}
            onChange={(event) => handleFilterChange("count", event.target.value)}
          />
        </div>
        <div className="col-md-1">
          <button
            type="button"
            className="btn btn-primary w-100"
            onClick={handleSearch}
            disabled={loading}
            title="Show report"
            aria-label="Show report"
          >
            <FaSearch aria-hidden="true" />
          </button>
        </div>
        <div className="col-md-1">
          <button
            type="button"
            className="btn btn-outline-success w-100"
            onClick={handleExportExcel}
            disabled={loading || rows.length === 0}
            title="Export Excel"
            aria-label="Export Excel"
          >
            <FaFileExcel aria-hidden="true" />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted small">
          Showing {rows.length} record{rows.length === 1 ? "" : "s"}
          {totalCount ? ` of ${totalCount}` : ""}
          {razorpayCount ? ` | Razorpay count: ${razorpayCount}` : ""}
        </div>
        <div className="text-muted small">Page {pageNumber}</div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th scope="col">#</th>
              <th scope="col" className="text-nowrap">Payment ID</th>
              <th scope="col" className="text-nowrap">Booking Ref</th>
              <th scope="col" className="text-nowrap">Status</th>
              <th scope="col" className="text-nowrap">Captured</th>
              <th scope="col" className="text-nowrap">Amount</th>
              <th scope="col" className="text-nowrap">Refunded</th>
              <th scope="col" className="text-nowrap">Refund Status</th>
              <th scope="col" className="text-nowrap">Customer</th>
              <th scope="col" className="text-nowrap">Email</th>
              <th scope="col" className="text-nowrap">Mobile</th>
              <th scope="col" className="text-nowrap">Hotel</th>
              <th scope="col" className="text-nowrap">Check In</th>
              <th scope="col" className="text-nowrap">Check Out</th>
              <th scope="col" className="text-nowrap">Created At</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="15" className="text-center py-4">
                  Loading payment report...
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((item, index) => (
                <tr key={item.paymentId || `${skip}-${index}`}>
                  <td>{skip + index + 1}</td>
                  <td className="text-nowrap">{item.paymentId || "-"}</td>
                  <td>{item.bookingRef || "-"}</td>
                  <td>
                    <span
                      className={`badge ${getStatusBadgeClass(
                        item.status
                      )} text-capitalize`}
                    >
                      {item.status || "-"}
                    </span>
                  </td>
                  <td>{item.captured ? "Yes" : "No"}</td>
                  <td>{formatAmount(item.amount)}</td>
                  <td>{formatAmount(item.amountRefunded)}</td>
                  <td>{item.refundStatus || "-"}</td>
                  <td>{item.customer?.name || "-"}</td>
                  <td>{item.customer?.email || "-"}</td>
                  <td>{item.customer?.mobile || "-"}</td>
                  <td>{item.hotel?.name || "-"}</td>
                  <td>{formatDate(item.stay?.checkIn)}</td>
                  <td>{formatDate(item.stay?.checkOut)}</td>
                  <td>{formatDate(item.createdAt, true)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="15" className="text-center py-4">
                  No payment records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          title="Previous page"
          aria-label="Previous page"
        >
          <FaChevronLeft aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleNext}
          disabled={!canGoNext}
          title="Next page"
          aria-label="Next page"
        >
          <FaChevronRight aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default PaymentReportTable;
